import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Layers, Plus, Sparkles, Loader2, CheckCircle2, X,
  Play, Settings, Clock, Globe, Link2, TrendingUp,
  Check, ImageIcon, Tag, ShoppingBag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { id: "custom", label: "Wlasne tematy" },
  { id: "local", label: "Tematy lokalne (miejscowosci)" },
  { id: "reviews", label: "Recenzje produktow" },
  { id: "news", label: "Aktualnosci i trendy" },
];

const TOPIC_TEMPLATES = {
  local: [
    '"Najlepsze {keyword} w [Miasto]"',
    '"Poradnik: Jak {keyword} w [Miasto]"',
    '"Gdzie znalezc {keyword} w [Miasto]"',
    '"Testy i porownanie {keyword} w [Miasto]"',
    '"Top 5 {keyword} w [Miasto] - ranking"',
    '"Przewodnik po {keyword} w [Miasto]"',
    '"Historia {keyword} w [Miasto]"',
    '"Wydarzenia {keyword} w [Miasto] 2026"',
  ],
  reviews: [
    '"Recenzja {product} - czy warto kupic?"',
    '"Porownanie {product} vs konkurencja"',
    '"Top 10 {product} - ranking 2026"',
    '"Test {product} po 30 dniach"',
    '"{product} - przewodnik kupujacego"',
  ],
  news: [
    '"Najnowsze trendy w {topic} - 2026"',
    '"Co sie zmienilo w {topic}?"',
    '"Prognoza {topic} na 2026"',
    '"Ekspertow zdanie o {topic}"',
    '"{topic} - co musisz wiedziec"',
  ],
};

const CATEGORIES = [
  "Technologia", "Biznes", "Finanse", "Bankowosc", "Zdrowie", "Medycyna",
  "Moda", "Uroda", "Kosmetyki", "Podroze", "Turystyka", "Motoryzacja",
  "Sport", "Fitness", "Edukacja", "Marketing", "SEO", "E-commerce",
  "Nieruchomosci", "Prawo", "Ekologia", "Jedzenie", "Kultura", "Rozrywka",
  "Gaming", "AI/ML", "Lifestyle", "Dom i Ogrod", "Parenting", "Zwierzeta"
];

export default function MassGenerate() {
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [batches, setBatches] = useState([]);
  const [activeTab, setActiveTab] = useState("generate");

  // Typ tresci
  const [contentType, setContentType] = useState("custom");
  const [category, setCategory] = useState("Technologia");

  // Tematy
  const [templateTopics, setTemplateTopics] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [generatedTopics, setGeneratedTopics] = useState([]);
  const [topicInput, setTopicInput] = useState("");

  // Ustawienia
  const [siteId, setSiteId] = useState("");
  const [wordCount, setWordCount] = useState(1200);
  const [tone, setTone] = useState("professional");
  const [autoSchedule, setAutoSchedule] = useState(false);
  const [spreadDays, setSpreadDays] = useState(7);
  const [autoPublishWp, setAutoPublishWp] = useState(false);
  const [affiliateProducts, setAffiliateProducts] = useState(false);
  const [supportArticles, setSupportArticles] = useState(false);
  const [supportCount, setSupportCount] = useState(3);
  const [generateImages, setGenerateImages] = useState(true);
  const [wpCategoryId, setWpCategoryId] = useState("");
  const [wpCategories, setWpCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [publishStatus, setPublishStatus] = useState("draft");
  const [imageAlt, setImageAlt] = useState("");

  // State
  const [suggesting, setSuggesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [sitesRes, batchRes] = await Promise.all([
        axios.get(`${API}/sites`),
        axios.get(`${API}/mass/batches`),
      ]);
      setSites(sitesRes.data);
      setBatches(batchRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pobierz kategorie WP gdy zmieni się strona
  useEffect(() => {
    const fetchWpCategories = async () => {
      if (!siteId) {
        setWpCategories([]);
        setWpCategoryId("");
        return;
      }
      setLoadingCategories(true);
      try {
        const res = await axios.get(`${API}/sites/wp/${siteId}/wp-categories`);
        setWpCategories(res.data || []);
        // Ustaw domyślną kategorię jeśli jest
        if (res.data && res.data.length > 0) {
          setWpCategoryId(res.data[0].id.toString());
        }
      } catch (err) {
        console.error("Błąd pobierania kategorii:", err);
        setWpCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchWpCategories();
  }, [siteId]);

  // Load template topics when content type changes
  useEffect(() => {
    if (contentType !== "custom") {
      const templates = TOPIC_TEMPLATES[contentType] || [];
      setTemplateTopics(templates);
      setSelectedTemplates(templates.map((_, i) => i)); // select all
    } else {
      setTemplateTopics([]);
      setSelectedTemplates([]);
    }
  }, [contentType]);

  const toggleTemplate = (idx) => {
    setSelectedTemplates(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleSuggestTopics = async () => {
    if (!siteId) {
      toast.warning("Wybierz stronę docelową aby AI wygenerowało odpowiednie tematy");
    }
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/mass/suggest`, {
        mode: contentType, 
        category, 
        count: 10, 
        ai_model: "gemini",
        site_id: siteId || null  // Przekaż stronę docelową
      });
      const topics = (res.data.topics || []).map(t2 => ({
        title: t2.title || t2,
        selected: true,
        description: t2.description || "",
        seo_potential: t2.seo_potential || null,
        traffic_keywords: t2.traffic_keywords || []
      }));
      setGeneratedTopics(topics);
      if (siteId) {
        const siteName = sites.find(s => s.id === siteId)?.name || "";
        toast.success(`${topics.length} tematów dla "${siteName}"`);
      } else {
        toast.success(`${topics.length} tematów wygenerowanych`);
      }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSuggesting(false); }
  };

  const addManualTopic = () => {
    if (topicInput.trim()) {
      setGeneratedTopics(prev => [...prev, { title: topicInput.trim(), selected: true }]);
      setTopicInput("");
    }
  };

  const toggleTopic = (idx) => {
    setGeneratedTopics(prev => prev.map((t2, i) =>
      i === idx ? { ...t2, selected: !t2.selected } : t2
    ));
  };

  const removeTopic = (idx) => {
    setGeneratedTopics(prev => prev.filter((_, i) => i !== idx));
  };

  const selectAll = () => setGeneratedTopics(prev => prev.map(t2 => ({ ...t2, selected: true })));
  const deselectAll = () => setGeneratedTopics(prev => prev.map(t2 => ({ ...t2, selected: false })));

  const selectedCount = generatedTopics.filter(t2 => t2.selected).length;
  const totalArticles = selectedCount;
  const totalSupport = supportArticles ? selectedCount * supportCount : 0;

  const handleGenerate = async () => {
    const topics = generatedTopics.filter(t2 => t2.selected).map(t2 => t2.title);
    if (topics.length === 0) { toast.error("Zaznacz tematy do wygenerowania"); return; }

    setGenerating(true);
    setResult(null);
    setProgress(5);

    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(p + 3, 90));
    }, 3000);

    try {
      const res = await axios.post(`${API}/mass/generate`, {
        topics,
        settings: {
          word_count: wordCount, language: "pl", tone,
          include_faq: true, include_internal_links: true,
          auto_schedule: autoSchedule, spread_days: spreadDays,
          auto_publish_wp: autoPublishWp,
          affiliate_products: affiliateProducts,
          support_articles: supportArticles,
          support_count: supportCount,
          generate_images: generateImages,
          wp_category_id: wpCategoryId || null,
          custom_image_alt: imageAlt || null,
        },
        ai_model: "gemini",
        site_id: siteId || null,
        save_as: publishStatus,
      }, { timeout: 300000 });
      setResult(res.data);
      setProgress(100);
      fetchData();
      toast.success(`Wygenerowano ${res.data.generated} artykulow`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      clearInterval(progressInterval);
      setGenerating(false);
    }
  };

  return (
    <div data-testid="mass-generate-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Masowa Generacja Tresci</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">Tworz masowo oryginalne artykuly z automatycznym linkowaniem i wsparciem SEO</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generate" className="gap-1.5"><Layers className="w-3.5 h-3.5" /> Generuj</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5"><Clock className="w-3.5 h-3.5" /> Historia <Badge variant="secondary" className="text-[10px] ml-1">{batches.length}</Badge></TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEWA - Tematy */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading">Tematy do Wygenerowania</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1 space-y-3">
                  {/* Typ tresci */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Typ tresci</Label>
                    <Select value={contentType} onValueChange={setContentType}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONTENT_TYPES.map(ct => <SelectItem key={ct.id} value={ct.id} className="text-xs">{ct.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Szablony tematow */}
                  {templateTopics.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Tematy {contentType === "local" ? "lokalne" : ""}:</Label>
                        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={handleSuggestTopics} disabled={suggesting}>
                          {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          AI zasugeruj tematy
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {templateTopics.map((tmpl, i) => (
                          <button key={i} onClick={() => toggleTemplate(i)}
                            className={`flex items-start gap-2 p-2.5 rounded-lg border text-left text-xs transition-all ${
                              selectedTemplates.includes(i)
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            }`}>
                            <div className={`w-4 h-4 rounded border-2 mt-0.5 shrink-0 flex items-center justify-center ${
                              selectedTemplates.includes(i) ? "bg-primary border-primary" : "border-slate-300"
                            }`}>
                              {selectedTemplates.includes(i) && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                            </div>
                            <span className="text-foreground">{tmpl}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-[9px] text-muted-foreground">AI wybierze 8-10 najlepszych miast i wygeneruje unikalne tematy bez duplikatow</p>
                    </div>
                  )}

                  {/* Reczne dodawanie */}
                  <div className="flex gap-2">
                    <Input value={topicInput} onChange={(e) => setTopicInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addManualTopic()}
                      placeholder="Wpisz temat reczny..." className="flex-1 text-xs" />
                    <Button variant="outline" size="sm" onClick={addManualTopic} className="shrink-0 text-xs gap-1">
                      <Plus className="w-3 h-3" /> Dodaj
                    </Button>
                    {!generatedTopics.length && (
                      <Button variant="outline" size="sm" onClick={handleSuggestTopics} disabled={suggesting} className="shrink-0 text-xs gap-1">
                        {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Generuj tematy
                      </Button>
                    )}
                  </div>

                  {/* Wygenerowane tematy */}
                  {generatedTopics.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-6 text-[9px]" onClick={selectAll}>Zaznacz wszystkie</Button>
                        <Button variant="outline" size="sm" className="h-6 text-[9px]" onClick={deselectAll}>Odznacz wszystkie</Button>
                        <Badge variant="secondary" className="text-xs ml-auto">{selectedCount} / {generatedTopics.length} zaznaczonych</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {generatedTopics.map((topic, i) => (
                          <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                            topic.selected
                              ? "bg-primary/10 border-primary/30 text-primary"
                              : "bg-muted/50 border-border text-muted-foreground"
                          }`}>
                            <button onClick={() => toggleTopic(i)}
                              className={`w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center ${
                                topic.selected ? "bg-primary border-primary" : "border-slate-300"
                              }`}>
                              {topic.selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                            </button>
                            <span className="truncate max-w-[280px]" title={topic.description || topic.title}>{topic.title}</span>
                            {topic.seo_potential && (
                              <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                                topic.seo_potential >= 8 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                topic.seo_potential >= 6 ? "bg-amber-50 text-amber-700 border-amber-200" :
                                "bg-slate-50 text-slate-600 border-slate-200"
                              }`}>
                                SEO:{topic.seo_potential}
                              </Badge>
                            )}
                            <button onClick={() => removeTopic(i)} className="shrink-0 hover:text-destructive">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Linkowanie wewnetrzne */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs font-medium">Linkowanie Wewnetrzne</span>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </CardContent>
              </Card>

              {/* Progress / Wynik */}
              {(generating || result) && (
                <Card className="rounded-xl border shadow-sm bg-card">
                  <CardContent className="p-5">
                    {generating ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-primary" />
                          <span className="text-sm font-medium">Generowanie {totalArticles} artykulow{totalSupport > 0 ? ` + ${totalSupport} wspierajacych` : ""}...</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                        <p className="text-[10px] text-muted-foreground">To moze potrwac kilka minut...</p>
                      </div>
                    ) : result ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          <span className="text-sm font-semibold">{result.generated} artykulow wygenerowanych{result.failed > 0 ? ` (${result.failed} nieudanych)` : ""}</span>
                        </div>
                        <ScrollArea className="max-h-[200px]">
                          {result.posts?.map((p, i) => (
                            <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-emerald-50 dark:bg-emerald-900/10 mb-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                              <span className="text-xs truncate">{p.title}</span>
                              <Badge variant="secondary" className="text-[9px] ml-auto shrink-0">{p.word_count} sl.</Badge>
                            </div>
                          ))}
                        </ScrollArea>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* PRAWA - Ustawienia */}
            <div>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <Settings className="w-4 h-4 text-muted-foreground" /> Ustawienia Generacji
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1 space-y-4">
                  {/* Strona docelowa */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Strona Docelowa</Label>
                    <Select value={siteId || "none"} onValueChange={(v) => setSiteId(v === "none" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Wybierz..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">--</SelectItem>
                        {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Kategoria WordPress - widoczna gdy wybrana strona */}
                  {siteId && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Kategoria WordPress</Label>
                      {loadingCategories ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" /> Ładowanie...
                        </div>
                      ) : wpCategories.length > 0 ? (
                        <Select value={wpCategoryId} onValueChange={setWpCategoryId}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Wybierz kategorię" /></SelectTrigger>
                          <SelectContent>
                            {wpCategories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id.toString()} className="text-xs">
                                {cat.name} ({cat.count || 0})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-[10px] text-amber-600">Brak kategorii lub błąd połączenia</p>
                      )}
                    </div>
                  )}

                  {/* Liczba slow */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Liczba Slow: {wordCount}</Label>
                    <Slider value={[wordCount]} onValueChange={([v]) => setWordCount(v)} min={500} max={3000} step={100} />
                  </div>

                  {/* Ton */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Ton Pisania</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional" className="text-xs">Profesjonalny</SelectItem>
                        <SelectItem value="casual" className="text-xs">Swobodny</SelectItem>
                        <SelectItem value="seo" className="text-xs">SEO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Generuj obrazki */}
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Generuj Obrazki AI</Label>
                    <Switch checked={generateImages} onCheckedChange={setGenerateImages} />
                  </div>

                  {/* Auto harmonogram */}
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Auto-Harmonogram</Label>
                    <Switch checked={autoSchedule} onCheckedChange={setAutoSchedule} />
                  </div>
                  {autoSchedule && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Rozloz na {spreadDays} dni</Label>
                      <Slider value={[spreadDays]} onValueChange={([v]) => setSpreadDays(v)} min={1} max={30} step={1} />
                    </div>
                  )}

                  <Separator />

                  {/* Auto-publikacja WP */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                      <Label className="text-xs">Auto-publikacja na WordPress</Label>
                    </div>
                    <Switch checked={autoPublishWp} onCheckedChange={setAutoPublishWp} />
                  </div>

                  {autoPublishWp && (
                    <div className="space-y-2 p-2 rounded-lg bg-muted/50">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Status publikacji</Label>
                        <Select value={publishStatus} onValueChange={setPublishStatus}>
                          <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft" className="text-xs">Szkic</SelectItem>
                            <SelectItem value="pending" className="text-xs">Oczekujący</SelectItem>
                            <SelectItem value="published" className="text-xs">Opublikowany</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Kategoria WordPress</Label>
                        {loadingCategories ? (
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin" /> Ładowanie kategorii...
                          </div>
                        ) : wpCategories.length > 0 ? (
                          <Select value={wpCategoryId} onValueChange={setWpCategoryId}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Wybierz kategorię" /></SelectTrigger>
                            <SelectContent>
                              {wpCategories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id.toString()} className="text-xs">
                                  {cat.name} ({cat.count || 0})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-[9px] text-amber-600">Wybierz stronę aby załadować kategorie</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Alt zdjęcia głównego (opcjonalnie)</Label>
                        <Input 
                          value={imageAlt} 
                          onChange={(e) => setImageAlt(e.target.value)} 
                          placeholder="Zostaw puste - AI wygeneruje automatycznie" 
                          className="h-7 text-[10px]" 
                        />
                      </div>
                      <p className="text-[8px] text-muted-foreground">Artykuły zostaną automatycznie opublikowane na WordPress</p>
                    </div>
                  )}

                  <Separator />

                  {/* Produkty afiliacyjne */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <ShoppingBag className="w-3.5 h-3.5 text-amber-500" />
                      <Label className="text-xs">Produkty Afiliacyjne</Label>
                    </div>
                    <Switch checked={affiliateProducts} onCheckedChange={setAffiliateProducts} />
                  </div>
                  {affiliateProducts && (
                    <p className="text-[8px] text-muted-foreground pl-5">AI automatycznie doda produkty afiliacyjne dopasowane do tematyki artykulu</p>
                  )}

                  <Separator />

                  {/* Artykuly wspierajace */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                      <Label className="text-xs">Artykuly Wspierajace (SEO Boost)</Label>
                    </div>
                    <Switch checked={supportArticles} onCheckedChange={setSupportArticles} />
                  </div>
                  {supportArticles && (
                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800">
                      <div className="space-y-1.5">
                        <Label className="text-[10px]">Ilosc artykulow wspierajacych: {supportCount}</Label>
                        <Slider value={[supportCount]} onValueChange={([v]) => setSupportCount(v)} min={1} max={5} step={1} />
                      </div>
                      <p className="text-[8px] text-muted-foreground mt-1">
                        AI wygeneruje dodatkowe artykuly z linkami do glownego artykulu i automatycznie opublikuje je na innych stronach
                      </p>
                    </div>
                  )}

                  <Separator />

                  {/* GENERUJ */}
                  <Button className="w-full h-11 gap-2 rounded-lg text-sm font-semibold"
                    onClick={handleGenerate} disabled={generating || selectedCount === 0}>
                    {generating ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generowanie...</>
                    ) : (
                      <><Play className="w-4 h-4" /> Generuj {totalArticles} Artykulow{totalSupport > 0 ? ` + ${totalSupport} Wspierajacych` : ""}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* HISTORIA */}
        <TabsContent value="history" className="mt-4">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {batches.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Layers className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">Brak historii generacji</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[400px]">
                  <div className="divide-y divide-border">
                    {batches.map((batch) => (
                      <div key={batch.id} className="flex items-center gap-3 p-4">
                        <Layers className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium">Batch: {batch.result?.generated || 0} artykulow</p>
                          <p className="text-[10px] text-muted-foreground">{batch.params?.ai_model?.toUpperCase()} - {new Date(batch.created_at).toLocaleString()}</p>
                        </div>
                        <Badge className={`text-[10px] border-0 rounded-md ${batch.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {batch.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
